import mapboxgl, { Map } from 'mapbox-gl';
import { Site } from 'src/api/Api';
import MapboxFeature from 'src/mapbox-helpers/MapboxFeature';
import { mapBoxConfig } from 'src/mapbox-helpers/config';
import { roundToTwoDecimalPlaces } from 'src/util/util';
import GeoJSON from 'geojson';
import { bbox } from '@turf/turf';
import { REPORTING_DEVICE_CONFIG } from './config';
import { getIconUrl } from './map-util';
import { ReportingDeviceConfig } from './map-types';

const runWhenStyleLoaded = (map: Map, cb: () => void) => {
  if (map.isStyleLoaded()) {
    cb();
  } else {
    map.once('load', cb);
  }
};

export const addSiteBoundary = (
  map: Map,
  site: Site,
  onMouseEnter?: (e: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] | undefined }) => void,
  onMouseLeave?: (e: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] | undefined }) => void,
  onMouseMove?: (e: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] | undefined }) => void,
) => {
  const { boundary } = site;
  if (!boundary) return;

  runWhenStyleLoaded(map, () => {
    const polygon = new MapboxFeature();
    const sourceId = `site-boundary-${site.site_id}`;

    polygon.add(boundary, {
      Name: site.site_name,
      Area: `${roundToTwoDecimalPlaces(site.area)}`,
    });

    // ...keep the rest of your existing logic the same:
    // remove old source/layers if needed, addSource, addLayer, event handlers
    const fillLayerId = `fill-${sourceId}`;
    if (map.getSource(sourceId)) {
      map.removeSource(sourceId);
    }
    map.addSource(sourceId, { type: 'geojson', data: polygon.toJSON() });

    if (map.getLayer(fillLayerId)) {
      map.removeLayer(fillLayerId);
    }
    map.addLayer({
      id: fillLayerId,
      type: 'fill',
      source: sourceId,
      paint: {
        'fill-color': mapBoxConfig.polygon.fillColor,
        'fill-opacity': mapBoxConfig.polygon.defaultPolygonOpacity,
      },
      filter: ['==', '$type', 'Polygon'],
    });

    if (onMouseEnter) map.on('mouseenter', fillLayerId, onMouseEnter);
    if (onMouseLeave) map.on('mouseleave', fillLayerId, onMouseLeave);
    if (onMouseMove) map.on('mousemove', fillLayerId, onMouseMove);
  });
};



export const fitMapToBoundaries = (map: Map, sites: Site[], hideAllControl?: boolean) => {
  if (!sites || sites.length === 0) return;

  const points: GeoJSON.Feature<GeoJSON.Point>[] = [];
  sites.forEach(site => {
    if (site.centroid_wkt) {
      const feature = MapboxFeature.parseFeatureFromString(site.centroid_wkt);
      if (feature && feature.geometry && feature.geometry.type === 'Point') {
        points.push(feature as GeoJSON.Feature<GeoJSON.Point>);
      }
    }
  });

  if (points.length === 0) {
    console.warn("No site markers available to fit bounds.");
    return;
  }

  if (points.length === 1) {
    const coordinates = points[0].geometry?.coordinates as [number, number];
    if (coordinates) {
      map.flyTo({
        center: coordinates,
        zoom: 12,
        animate: true,
        duration: 2000,
      });
    }
  } else {
    const featureCollection: GeoJSON.FeatureCollection<GeoJSON.Point> = {
      type: "FeatureCollection",
      features: points,
    };
    try {
      const calculatedBbox = bbox(featureCollection);
      map.fitBounds(calculatedBbox as [number, number, number, number], {
        padding: {
          top: hideAllControl ? 10 : 150,
          bottom: hideAllControl ? 10 : 150,
          left: hideAllControl ? 70 : 250,
          right: hideAllControl ? 70 : 250,
        },
        maxZoom: 12,   // clamp so it never zooms too far in
        animate: true,
        duration: 1500,
      });
    } catch (e) {
      console.error("Error calculating bounding box for site markers:", e);
    }
  }
};

export const addSiteBoundaryMarker = (map: Map, site: Site, markerArr?: mapboxgl.Marker[]) => {
  if (!site.centroid_wkt) return;
  const point = site.centroid_wkt;
  const geojson = MapboxFeature.parseFeatureFromString(point);
  const coordinates = (geojson?.geometry as GeoJSON.Point)?.coordinates as [number, number];
  if (coordinates) {
    const marker = new mapboxgl.Marker({ color: mapBoxConfig.marker.color, scale: mapBoxConfig.marker.scale })
      .setLngLat(coordinates)
      .addTo(map);
    if (markerArr) {
      markerArr.push(marker);
    }
  }
};

export const addDeviceMarker = (map: Map, site: Site, key: string) => {
  const deviceType: ReportingDeviceConfig = REPORTING_DEVICE_CONFIG[key];
  if (!site || !deviceType || !key) return;

  runWhenStyleLoaded(map, () => {
    const device = new MapboxFeature();
    // @ts-expect-error (site[key] is dynamic)
    device.add(site[key], {
      Name: site.site_name,
      // Type: deviceType.name,
      Area: `${roundToTwoDecimalPlaces(site.area)} `,
    });

    const geoJSON = device.toJSON() as GeoJSON.FeatureCollection<
      GeoJSON.Geometry,
      GeoJSON.GeoJsonProperties
    >;

    map.addSource(deviceType.type, {
      type: 'geojson',
      data: geoJSON,
    });

    geoJSON.features.forEach((feature) => {
      if (!feature.geometry) return;

      const createMarker = (coords: [number, number]) => {
        const resolveIconUrl = getIconUrl(deviceType.icon);
        const customElement = document.createElement('div');
        customElement.innerHTML = `<img src="${resolveIconUrl}" style="width: ${mapBoxConfig.icon.width}px; height: ${mapBoxConfig.icon.height}px;" alt="${deviceType.name}">`;
        new mapboxgl.Marker({ element: customElement }).setLngLat(coords).addTo(map);
      };

      if (feature.geometry.type === 'Point') {
        const coordinates = feature.geometry.coordinates as [number, number];
        createMarker(coordinates);
      } else if (feature.geometry.type === 'MultiPoint') {
        const points = feature.geometry.coordinates as [number, number][];
        points.forEach(createMarker);
      }
    });
  });
};
